import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PotreeCesiumViewerService } from 'src/app/components/potree-cesium-viewer/potree-cesium-viewer.service';
import { AnnotationService } from 'src/app/services/annotation/annotation.service';
import { MapService } from 'src/app/services/map/map.service';
import { SplitViewService } from 'src/app/services/split-view/split-view.service';
import { LocationStoreService } from '../location-store/location-store.service';
import { AnnotationStoreService } from '../annotation-store/annotation-store.service';

@Injectable({
  providedIn: 'root',
})
export class LayerStoreService {
  componentFilter = [];
  defectFilter = [];
  severityFilter = [];

  annotationServiceSub: Subscription;
  isThreeD: boolean;
  isThreeDMesh: boolean;
  isEmbed: boolean;
  isGeojson: boolean;
  isOrtho: boolean;
  isDSM: boolean;
  isThermal: boolean;
  isSatellite: boolean;
  isAnnotation: boolean;
  isFacade: boolean;

  componentFilter$ = new BehaviorSubject([]);
  defectFilter$ = new BehaviorSubject([]);
  severityFilter$ = new BehaviorSubject([]);
  isThreeD$ = new BehaviorSubject(false);
  isThreeDMesh$ = new BehaviorSubject(false);
  isEmbed$ = new BehaviorSubject(false);
  isGeojson$ = new BehaviorSubject(false);
  isOrtho$ = new BehaviorSubject(false);
  isDSM$ = new BehaviorSubject(false);
  isThermal$ = new BehaviorSubject(false);
  isSatellite$ = new BehaviorSubject(false);
  isAnnotation$ = new BehaviorSubject(false);
  isFacade$ = new BehaviorSubject(null);

  embedLayers$ = new BehaviorSubject([]);
  embedLayerFilter$ = new BehaviorSubject([]);

  geojsonLayers$ = new BehaviorSubject([]);
  geojsonLayerFilter$ = new BehaviorSubject([]);
  orthoManyLayerFilter$ = new BehaviorSubject([]);

  constructor(
    private annotationService: AnnotationService,
    private potreeCesiumViewerService: PotreeCesiumViewerService,
    private splitViewService: SplitViewService,
    private mapService: MapService,
    private locationStore: LocationStoreService,
    private annotationStore: AnnotationStoreService
  ) {}

  set setComponentFilter(data) {
    this.componentFilter = data;
  }

  set setDefectFilter(data) {
    this.defectFilter = data;
  }

  set setSeverityFilter(data) {
    this.severityFilter = data;
  }

  set setIsOrtho(data) {
    this.isOrtho = data;
  }

  set setIsDSM(data) {
    this.isDSM = data;
  }

  set setIsThermal(data) {
    this.isThermal = data;
  }

  set setIsThreeD(data) {
    this.isThreeD = data;
  }

  set setIsThreeDMesh(data) {
    this.isThreeDMesh = data;
  }

  set setIsEmbed(data) {
    this.isEmbed = data;
  }

  set setIsGeojson(data) {
    this.isGeojson = data;
  }

  /**
   * Toggles annotation on-off, affects the toggle in Annotations layer filter toggle
   * @param category Generic type category to toggle by
   * @param id Generic type id to toggle
   * @param isInverse Reverses the toggle and select only one
   * @param force Forces the an id to be selected regardless of its state. Use with care
   */
  toggleAnnotationBy(fnArg: {
    category: 'location_component' | 'annotation_type' | 'severity_type';
    id: string;
    isInverse?: boolean;
    force?: boolean;
    skipZoom?: boolean;
  }) {
    const { category, id, isInverse, force, skipZoom } = fnArg;

    if (this.annotationServiceSub) this.annotationServiceSub.unsubscribe();
    let pos = -1;

    /**
     * Force the filter rather than toggling
     * @param gTypeId Generic Type Id to force add
     * @param arr Array that the gTypeId will be included in
     * @param remove Forcibly remove the gTypeId from arr
     */
    const forceFn = (gTypeId: string, arr: string[], remove?: boolean) => {
      if (!gTypeId) return;
      if (!remove) {
        if (arr.indexOf(gTypeId) > -1) return;
        else arr.push(gTypeId);
      } else {
        const idx = arr.indexOf(gTypeId);
        if (idx > -1) arr.splice(idx, 1);
      }
    };

    switch (category) {
      case 'location_component':
        if (force) {
          if (!isInverse) forceFn(id, this.componentFilter);
          else forceFn(id, this.componentFilter, true);
        } else if (isInverse) this.componentFilter = [id];
        else {
          pos = this.componentFilter.indexOf(id);
          if (pos > -1) this.componentFilter.splice(pos, 1);
          else this.componentFilter.push(id);
          if (isInverse) this.componentFilter = [id];
        }

        this.componentFilter$.next(this.componentFilter);
        break;
      case 'annotation_type':
        if (force) {
          if (!isInverse) forceFn(id, this.defectFilter);
          else forceFn(id, this.defectFilter, true);
        } else if (isInverse) this.defectFilter = [id];
        else {
          pos = this.defectFilter.indexOf(id);
          if (pos > -1) this.defectFilter.splice(pos, 1);
          else this.defectFilter.push(id);
        }

        this.defectFilter$.next(this.defectFilter);
        break;
      case 'severity_type':
        if (force) {
          if (!isInverse) forceFn(id, this.severityFilter);
          else forceFn(id, this.severityFilter, true);
        } else if (isInverse) this.severityFilter = [id];
        else {
          pos = this.severityFilter.indexOf(id);
          if (pos > -1) this.severityFilter.splice(pos, 1);
          else this.severityFilter.push(id);
        }

        this.severityFilter$.next(this.severityFilter);
        break;
    }
    let params = [
      { key: 'filtered[0][key]', val: 'location' },
      {
        key: 'filtered[0][val]',
        val: this.locationStore.currLocation$.getValue().id,
      },
    ];
    //filter by annotation type
    params.push({ key: 'filtered[1][key]', val: 'annotation_type' });
    params.push(
      ...this.defectFilter.map((i, k) => {
        return { key: `filtered[1][val][${k}]`, val: i };
      })
    );
    //filter by severity type
    params.push({ key: 'filtered[2][key]', val: 'severity_type' });
    params.push(
      ...this.severityFilter.map((i, k) => {
        return { key: `filtered[2][val][${k}]`, val: i };
      })
    );
    params.push({ key: 'sorted[0][key]', val: 'created_at' });
    params.push({ key: 'sorted[0][val]', val: '-1' });

    this.annotationServiceSub = this.annotationService
      .findAll(params)
      .subscribe((res) => {
        // const data3d = res.data.filter(annot => annot.map_layer?.layer_type === '3d')
        // this.potreeCesiumViewerService.loadMeasurements(data3d);
        // this.dataService.annotations$.next(res.data);
        let filteredAnnots = res.data || [];

        if (this.splitViewService.isSplitscreenOn)
          filteredAnnots = res.data.filter(
            (annot) =>
              annot.map_layer?.id === this.splitViewService.selectedOrtho.id
          );

        this.mapService.toggleAllAnnotation(
          true,
          undefined,
          filteredAnnots,
          skipZoom
        );
      });
  }

  toggleEmbedLayer(id: string) {
    let embedLayerFilter = this.embedLayerFilter$.getValue();
    let pos = -1;

    pos = embedLayerFilter.indexOf(id);
    if (pos > -1) embedLayerFilter.splice(pos, 1);
    else embedLayerFilter = [id];

    this.embedLayerFilter$.next(embedLayerFilter);
  }

  toggleGeojsonLayer(id: string) {
    let geojsonLayerFilter = this.geojsonLayerFilter$.getValue();
    let pos = -1;

    pos = geojsonLayerFilter.indexOf(id);
    if (pos > -1) {
      geojsonLayerFilter.splice(pos, 1);
      this.mapService.removeGeoJsons(undefined, id);
    } else geojsonLayerFilter.push(id);

    this.geojsonLayerFilter$.next(geojsonLayerFilter);
  }

  toggleOrthoManyLayer(ortho) {
    console.log('who trigger this?');
    if (!ortho) return;
    let orthoManyLayerFilter = this.orthoManyLayerFilter$.getValue();
    let pos = -1;

    pos = orthoManyLayerFilter.indexOf(ortho.id);
    if (pos > -1) {
      orthoManyLayerFilter.splice(pos, 1);
      this.mapService.removeOrtho(ortho.id);
    } else {
      orthoManyLayerFilter.push(ortho.id);
      console.log('addOrtho1');
      this.mapService.addOrtho(ortho, ortho.id);
    }

    this.orthoManyLayerFilter$.next(orthoManyLayerFilter);
  }

  toggleMapLayer(type, location_id, map_layer, force?: boolean) {
    if (force) {
      this.resetMapLayer();
    }
    switch (type) {
      case '3d':
        this.isThreeD = !this.isThreeD;
        if (typeof force === 'boolean') this.isThreeD = force;
        this.isThreeD$.next(this.isThreeD);
        if (this.isThreeD) {
          this.potreeCesiumViewerService
            .loadPointCloud(map_layer.external_url)
            .then((cloudjsData) => {
              this.potreeCesiumViewerService.viewerIsActive$.next(true);
            });
        } else {
          this.potreeCesiumViewerService.unloadAllPointClouds();
        }
        break;

      case 'facade':
        this.isFacade = !this.isFacade;
        if (typeof force === 'boolean') this.isFacade = force;
        if (this.isFacade) {
          this.isFacade$.next(map_layer);
        } else {
          this.isFacade$.next(null);
        }
        break;

      case 'ortho':
        this.isOrtho = !this.isOrtho;
        if (typeof force === 'boolean') this.isOrtho = force;

        this.isOrtho$.next(this.isOrtho);
        console.log(map_layer);
        // if (this.isOrtho) {
        //   this.geojsonLayers$.next(map_layer);
        // } else {
        //   this.geojsonLayers$.next([]);
        // }
        // this.isOrtho$.next(this.isOrtho);
        // if (this.isOrtho) {
        //   this.mapService.addOrtho(map_layer, location_id);
        // } else {
        //   this.mapService.removeOrtho(location_id);
        // }
        break;

      case 'dsm':
        this.isDSM = !this.isDSM;
        if (typeof force === 'boolean') this.isDSM = force;
        this.isDSM$.next(this.isDSM);
        if (this.isDSM) {
          this.mapService.addOrtho(map_layer, map_layer.id);
        } else {
          this.mapService.removeDSM(location_id);
        }
        break;

      case 'thermal':
        this.isThermal = !this.isThermal;
        if (typeof force === 'boolean') this.isThermal = force;
        this.isThermal$.next(this.isThermal);
        if (this.isThermal) {
          this.mapService.addOrtho(map_layer, map_layer.id);
        } else {
          this.mapService.removeThermal(location_id);
        }
        break;

      case '3d-mesh':
        this.isThreeDMesh = !this.isThreeDMesh;
        if (typeof force === 'boolean') this.isThreeDMesh = force;
        this.isThreeDMesh$.next(this.isThreeDMesh);
        if (this.isThreeDMesh) {
          this.mapService.addThreeDMesh(map_layer.external_url, location_id);
        } else {
          this.mapService.removeThreeDMesh(location_id);
        }
        break;

      case 'embed':
        this.isEmbed = !this.isEmbed;
        if (typeof force === 'boolean') this.isEmbed = force;
        this.isEmbed$.next(this.isEmbed);
        if (this.isEmbed) {
          this.embedLayers$.next(map_layer);
        } else {
          this.embedLayers$.next([]);
        }
        break;

      case 'geojson':
        this.isGeojson = !this.isGeojson;
        if (typeof force === 'boolean') this.isGeojson = force;
        this.isGeojson$.next(this.isGeojson);
        if (this.isGeojson) {
          this.geojsonLayers$.next(map_layer);
          setTimeout(() => {
            var toggler = document.getElementsByClassName('caret');
            for (let i = 0; i < toggler.length; i++) {
              toggler[i].addEventListener('click', function () {
                this.parentElement
                  .querySelector('.nested')
                  .classList.toggle('active');
                this.classList.toggle('caret-down');
              });
            }
          });
        } else {
          this.geojsonLayers$.next([]);
        }
        break;

      default:
        break;
    }
  }

  resetMapLayer() {
    if (!this.mapService.isMapLoaded$.getValue()) return;

    // this.isOrtho$.next(false);
    // this.isThermal$.next(false);
    // this.isDSM$.next(false);
    this.isThreeD$.next(false);
    this.isThreeDMesh$.next(false);
    this.isEmbed$.next(false);
    // this.isGeojson$.next(false);
    this.isFacade$.next(false);
    this.potreeCesiumViewerService.unloadAllPointClouds();
    this.mapService.removeAllGrid();
    this.mapService.removeAllOrtho();
    this.mapService.removeAllThermal();
    this.mapService.removeAllDSM();
    // this.mapService.removeGrid()
    this.mapService.toggleGalleryImages(false);
    // this.mapService.removeGeoJsons();
    if (this.mapService.markerDataGalleryImage)
      this.mapService.markerDataGalleryImage.remove();
    this.orthoManyLayerFilter$.next([]);
  }

  toggleSatellite() {
    this.isSatellite = !this.isSatellite;
    this.isSatellite$.next(this.isSatellite);
    this.mapService.toggleSatellite(this.isSatellite);
  }

  toggleAllAnnotationDataLayer(force?: boolean) {
    this.isAnnotation = typeof force === 'boolean' ? force : !this.isAnnotation;
    this.isAnnotation$.next(this.isAnnotation);
    const annotations = this.annotationStore.annotations$.getValue();

    /** If splitscreen, populate all annotations based on the maps and ortho available in splitview */
    if (this.splitViewService.isSplitscreenOn) {
      for (const mapView of this.splitViewService.mapViews) {
        if (mapView.ortho)
          this.mapService.toggleAllAnnotation(
            this.isAnnotation,
            mapView.map,
            annotations.filter(
              (annot) => annot?.map_layer?.id === mapView.ortho.id
            ),
            annotations.length === 0
          );
      }
      // this.splitViewService.mapViews.forEach((map, idx) => {
      //   const ortho = this.splitViewService.orthos[idx]
      //   if (ortho)
      //     this.mapService.toggleAllAnnotation(this.isAnnotation, map, annotations.filter(annot => annot?.map_layer?.id === ortho.id))
      // })
    } else
      this.mapService.toggleAllAnnotation(
        this.isAnnotation,
        undefined,
        annotations,
        annotations.length === 0
      );
  }
}
